import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MapPin, Building2, Mail, Sparkles, ArrowRight, ChevronRight, Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * A polished "Coming Soon" component for neighborhoods without content
 * Shows when a neighborhood has no experiences, events, or venues yet
 */
const ComingSoon = ({ 
  neighborhoodName = 'this area',
  neighborhoodSlug = '',
  type = 'experiences', // 'experiences', 'events', 'venues', 'full'
  compact = false,
  showSuggestionCTA = true,
  showExploreOther = true
}) => {
  const navigate = useNavigate();
  
  // Different content based on type
  const content = {
    experiences: {
      icon: Sparkles,
      title: `Exclusive Experiences Coming to ${neighborhoodName}`,
      subtitle: "We're curating unforgettable moments with local partners",
      features: [
        { icon: Users, text: 'Connecting with top venues' },
        { icon: Clock, text: 'Launching exclusive offers' },
        { icon: Sparkles, text: 'Crafting unique experiences' }
      ]
    },
    events: {
      icon: Building2,
      title: `Events Coming to ${neighborhoodName}`,
      subtitle: "Exciting happenings are on the way",
      features: [
        { icon: Users, text: 'Partnering with event organizers' },
        { icon: Clock, text: 'Building the calendar' },
        { icon: Sparkles, text: 'Curating memorable nights' }
      ]
    },
    venues: {
      icon: Building2,
      title: `Venues Coming to ${neighborhoodName}`,
      subtitle: "The best spots are joining soon",
      features: [
        { icon: Users, text: 'Onboarding local favorites' },
        { icon: Clock, text: 'Verifying quality standards' },
        { icon: Sparkles, text: 'Preparing special offers' }
      ]
    },
    full: {
      icon: MapPin,
      title: `EVENT★GOERS is Coming to ${neighborhoodName}`,
      subtitle: "Help us bring the best nightlife experiences to your neighborhood",
      features: [
        { icon: Users, text: 'Building venue partnerships' },
        { icon: Clock, text: 'Launching within weeks' },
        { icon: Sparkles, text: 'Early access for locals' }
      ]
    }
  };
  
  const { icon: Icon, title, subtitle, features } = content[type] || content.experiences;
  
  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-8 px-4 bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl border border-slate-700/50 backdrop-blur-sm"
        data-testid="coming-soon-compact"
      >
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center border border-indigo-500/30">
          <Icon className="w-7 h-7 text-indigo-400" />
        </div>
        <h3 className="text-lg font-bold text-slate-200 mb-2">Coming Soon</h3>
        <p className="text-sm text-slate-400 mb-4 max-w-xs mx-auto">{subtitle}</p>
        {showExploreOther && (
          <Button
            size="sm"
            variant="outline"
            className="border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10"
            onClick={() => navigate('/locations')}
          >
            Explore Other Areas
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </motion.div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-3xl border border-slate-700/50"
      data-testid="coming-soon-full"
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
      </div>
      
      <div className="relative z-10 p-8 sm:p-12 lg:p-16">
        <div className="max-w-3xl mx-auto text-center">
          {/* Icon with glow effect */}
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', bounce: 0.5 }}
            className="mb-6"
          >
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full blur-xl opacity-50" />
              <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 flex items-center justify-center backdrop-blur-sm">
                <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-indigo-400" />
              </div>
            </div>
          </motion.div>
          
          {/* Badge */}
          <Badge className="mb-4 bg-indigo-500/20 text-indigo-300 border-indigo-500/30 px-3 py-1">
            <MapPin className="w-3 h-3 mr-1" />
            {neighborhoodName}
          </Badge>
          
          {/* Title */}
          <h2 className="headline-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            {title}
          </h2>
          
          {/* Subtitle */}
          <p className="text-base sm:text-lg text-slate-400 mb-8 max-w-xl mx-auto">
            {subtitle}
          </p>
          
          {/* Feature pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.3 }}
              >
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700/50 text-sm text-slate-300">
                  <feature.icon className="w-4 h-4 text-indigo-400" />
                  <span>{feature.text}</span>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {showExploreOther && (
              <Button
                size="lg"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white border-0"
                onClick={() => navigate('/locations')}
              >
                Explore Other Neighborhoods
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            
            <Button
              size="lg"
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800/50"
              onClick={() => navigate('/marketplace')}
            >
              Browse All Experiences
            </Button>
          </div>
          
          {/* Suggestion CTA */}
          {showSuggestionCTA && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-10 pt-8 border-t border-slate-700/50"
            >
              <p className="text-slate-500 text-sm mb-3">
                Know a great spot in {neighborhoodName}?
              </p>
              <Button
                variant="link"
                className="text-indigo-400 hover:text-indigo-300"
                onClick={() => navigate('/locations')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Suggest a Venue
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Empty state card for sections that have no content
 * More lightweight than full ComingSoon component
 */
export const EmptyStateCard = ({
  icon: Icon = Sparkles,
  title = 'No items yet',
  description = 'Check back soon for new content',
  ctaText = 'Browse All',
  ctaAction,
  gradient = 'from-indigo-500/10 to-purple-500/10'
}) => {
  return (
    <Card className={`bg-gradient-to-br ${gradient} border-slate-700/50`}>
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800/50 flex items-center justify-center border border-slate-700/50">
          <Icon className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-300 mb-2">{title}</h3>
        <p className="text-sm text-slate-400 mb-4">{description}</p>
        {ctaAction && (
          <Button
            variant="outline"
            size="sm"
            className="border-slate-600 text-slate-300 hover:bg-slate-800/50"
            onClick={ctaAction}
          >
            {ctaText}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ComingSoon;
